import dayjs          from "dayjs";
import { useRef }     from "react";
import { Waypoint }   from "react-waypoint";
import useSWRInfinite from "swr/infinite";

import { useGetDeviceType } from "_common/hooks/global";
import Icon                 from "_components/atoms/icon";
import RooterTabs           from "_components/atoms/rooterTabs/presentation";
import Typography           from "_components/atoms/typography";
import { getUserDonations } from "_services/api/user";

import { zeroPad } from "../creatorLeaderboard/presentation";

import useStyles from "./styles";

const UserCard = ( { rank, name, message, amount } ) => {

	const { classes, cx } = useStyles ( {}, { name: "donationUserCard" } );

	return (
		<div className = { classes.userCardContainer }>
			<div className = { cx ( classes.rowCenter, classes.userLeft ) }>
				{
					typeof rank !== "number" ? (
						<Typography
							className = { classes.rank }
							title     = { "-" }
						/>
					) :
						rank > 3 ?
							(
								<Typography
									className = { classes.dimmed }
									ta        = "center"
									title     = { `#${ zeroPad ( rank, 2 ) }` }
									w         = "16px"
									weight    = "bold"
								/>
							)
							:
							rank === 1 ? (
								<Icon sameInBothTheme
									name = "fanRank1"
									size = { 16 }
								/>
							)
								: rank === 2 ? (
									<Icon sameInBothTheme
										name = "fanRank2"
										size = { 16 }
									/>
								)
									: (
										<Icon sameInBothTheme
											name = "fanRank3"
											size = { 16 }
										/>
									)
				}

			</div>

			<div className = { cx ( classes.rowCenter, classes.userMiddle ) }>
				<Typography
					title  = { name }
					weight = "500"
				/>

				<Typography
					className = { cx ( classes.dimmed, classes.message ) }
					lh        = "1.8rem"
					lineClamp = { 2 }
					size      = "sm"
					title     = { message }
				/>
			</div>

			<div className = { cx ( classes.rowCenter, classes.userRight ) }>
				<Typography
					title  = { amount > 0 ? `₹ ${ amount }` : "" }
					weight = "500"
				/>

			</div>
		</div>
	);
};

const fetchStreamerDonations = async params => {
	const { pageNo, streamerId, donatedAfter } = params;

	const res = await getUserDonations ( streamerId, donatedAfter, pageNo );

	return res;
};

const DonationsList = ( { streamerId, donatedAfter } ) => {
	const deviceType              = useGetDeviceType ();
	const isMobile                = deviceType === "mobile";
	const { classes, cx }         = useStyles ( { isMobile }, { name: "donationsList" } );
	const { data, size, setSize } = useSWRInfinite ( ( index, previousPageData ) => {
		if ( previousPageData && !previousPageData.length ) return null;

		return ( {
			pageNo       : index + 1,
			streamerId   : streamerId,
			donatedAfter : donatedAfter

		} );
	}, fetchStreamerDonations,
	{
		revalidateIfStale     : false,
		revalidateFirstPage   : false,
		revalidateOnFocus     : false,
		revalidateOnReconnect : false
	}
	);

	const flattenedData = data?.flat ( 2 ) || [];

	return (
		<div
			style = { {
				display       : "flex",
				flexDirection : "column",
				overflow      : "auto",
				                    // height        : "fit-content"
				height        : "50vh" // IMPORTANT : else waypoint doesn't work
			} }
		>
			{
				flattenedData.length > 0 ? flattenedData.map ( ( user, index ) => {
											  // return users.map ( ( user, idx ) => {
					const rank = index + 1;

					return (
						<UserCard
							key     = { index }
							amount  = { user.amount }
							message = { user.comment }
							name    = { user.name }
							rank    = { rank }
						/>
					);

				} ) : (
					<div className = { cx ( classes.emptyView, classes.rowCenter ) }>
						<Icon
							sameInBothTheme
							className = { classes.emptySvg }
							name      = "emptyDonation"
							size      = { 72 }
						/>

						<Typography
							className = { classes.dimmed }
							title     = { "Donate now to get featured here" }
						/>
					</div>
				)
			}

			{ flattenedData.length > 0 ? (
				<Waypoint
					bottomOffset = { "-10px" }
					onEnter      = { () => {
						setSize ( size + 1 );
					} }
				/>
			) : null}
		</div>
	);
};

const DonationHistory = ( { streamerId } ) => {
	const deviceType      = useGetDeviceType ();
	const isMobile        = deviceType === "mobile";
	const { classes, cx } = useStyles ( { isMobile }, { name: "donationHistory" } );

	const donorsRef = useRef ( null );

	return (
		<div className = { classes.historyContainer }>
			<div
				ref       = { donorsRef }
				className = { classes.heading }
			>
				<Icon
					sameInBothTheme
					name = "profileGift"
					size = { 24 }
				/>

				<Typography
					className = { classes.topDonors }
					component = "h2"
					size      = "lg"
					title     = "Top Donors"
					weight    = "500"
				/>
			</div>

			<RooterTabs
				defaultValue = "allTime"
				keepMounted  = { false }
			>
				<RooterTabs.List>
					<RooterTabs.Tab value = "thisMonth">
						This Month
					</RooterTabs.Tab>

					<RooterTabs.Tab value = "lastMonth">
						Last Month
					</RooterTabs.Tab>

					<RooterTabs.Tab value = "allTime">
						All Time
					</RooterTabs.Tab>
				</RooterTabs.List>

				<RooterTabs.Panel value = "thisMonth">

					<DonationsList
						donatedAfter = { dayjs ().format ( "YYYY-MM-01 00:00:00" ) }
						streamerId   = { streamerId }
					/>

				</RooterTabs.Panel>

				<RooterTabs.Panel value = "lastMonth">

					<DonationsList
						donatedAfter = { dayjs ().subtract ( 1, "month" ).format ( "YYYY-MM-01 00:00:00" ) }
						streamerId   = { streamerId }
					/>

				</RooterTabs.Panel>

				<RooterTabs.Panel value = "allTime">

					<DonationsList
						donatedAfter = { "" }
						streamerId   = { streamerId }
					/>

				</RooterTabs.Panel>
			</RooterTabs>
		</div>
	);
};

export default DonationHistory;